"use client";

import React from "react";
import {Button} from "@/components/ui/button";
import {MinusIcon, Percent, PlusIcon, Trash2} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import {useCart} from "@/store/shipping-cart/useCart";
import NoImage from "@/public/no-image.jpg";
import shoppingBagIcon from "@/public/icons/bag-2.svg"
import toast from "react-hot-toast";


function BagButtonComponent({hide = false}: { hide: boolean }) {

    const {items, removeFromCart, updateItemQuantity} = useCart();


    const handleUpdateQuantity = (unique_id: string, quantity: number, max_basket_limit: number, min_total_qty_and_max_basket_qty: number) => {
        const limit = Math.min(max_basket_limit, min_total_qty_and_max_basket_qty);
        if (quantity > limit) {
            toast.error(`بیشتراز ${limit} عدد نمیتوانید به سبد خرید خود اضافه کنید`, {
                position: "top-center",
            });
            return;
        }
        updateItemQuantity(unique_id, quantity);
    };

    const handleRemoveItem = (unique_id: string) => {
        removeFromCart(unique_id);
    };

    const activeItems = Array.isArray(items) ? items.filter((item: any) => item?.active) : [];
    const containerClasses = "w-9 h-9 group flex justify-center items-center rounded-xl relative duration-300 ";
    const iconClasses = "duration-100";

    return (
        <div>
            <div className={containerClasses}>
                <Link href={"/shopping-bag"} className="relative">
                    {activeItems?.length > 0 && (
                        <span
                            className="flex items-center absolute -right-1 -top-1 justify-center w-[16px] h-[16px] rounded-full bg-orange-primary text-[8px] font-extrabold text-white">
                            {activeItems?.length?.toLocaleString("fa")}
                        </span>
                    )}
                    <Image
                        src={shoppingBagIcon}
                        alt={"سبد خرید"}
                        title={"سبد خرید"}
                        className={iconClasses}
                    />
                </Link>
                {hide || activeItems?.length > 0 && (
                    <section
                        className="absolute top-10 invisible opacity-0 w-[461px] h-[528px] pb-20 shadow-shopping-bag bg-white z-[1000] duration-300 rounded-[20px] left-0 group-hover:opacity-100 group-hover:visible">
                        <div className="w-full h-full overflow-y-scroll">
                            <div className="w-full h-[50px] p-5 text-sm text-past-price text-start">
                                {items?.length.toLocaleString("fa")} کالا
                            </div>
                            {items?.map((item: any, i) => (
                                <div key={i}
                                     className="h-[230px] w-full overflow-hidden p-5 flex flex-wrap !justify-start">
                                    <div className="w-[120px] h-[120px]">
                                        <Image
                                            src={item.image ??  NoImage}
                                            title={item?.title ?? "کالا"}
                                            alt={item?.title ?? "image-product"}
                                            width={120}
                                            height={120}
                                            className="object-cover"
                                        />
                                    </div>
                                    <Link href={`/product/${item?.unique_id}/${item?.slug}`} className="w-[280px]">
                                        <div className="text-base w-full flex text-wrap text-start">{item?.title}</div>
                                        <div className="w-full flex mt-3">
                                            <span className="text-gray-400">فروشنده :</span>
                                            <strong>{item?.seller_title}</strong>
                                        </div>
                                    </Link>
                                    <div className="w-full flex justify-between pt-2 pb-6 items-end">
                                        <div
                                            className="w-[200px] border items-center px-2 justify-between rounded-xl border-orange-primary/25 h-12 flex flex-wrap">
                                            <div className="text-gray-400">تعداد:</div>
                                            <Button
                                                variant="outline"
                                                onClick={() => handleUpdateQuantity(item?.unique_id, item?.quantity + 1, item?.max_basket_limit, item?.min_total_qty_and_max_basket_qty)}
                                                className="p-1 w-7 h-7 border-0  m-0"
                                            >
                                                <PlusIcon className="w-6 h-6"/>
                                            </Button>

                                            <div
                                                className="w-6 flex items-center justify-center h-6 text-lg text-orange-primary">
                                                {item?.quantity?.toLocaleString("fa")}
                                            </div>
                                            {item?.quantity > 1 ? (
                                                <Button
                                                    onClick={() => handleUpdateQuantity(item?.unique_id, item?.quantity - 1, item?.max_basket_limit, item?.min_total_qty_and_max_basket_qty)}
                                                    variant={"outline"} className="p-1 w-7 h-7 border-0  m-0">
                                                    <MinusIcon className={"w-6 h-6"}/>
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={() => handleRemoveItem(item?.unique_id)}
                                                    variant="outline"
                                                    className="p-1 border-0 w-7 h-7 m-0"
                                                >
                                                    <Trash2 className="w-6 h-6"/>
                                                </Button>
                                            )}
                                        </div>

                                        <div className="w-2/3 flex flex-wrap pe-2">
                                            {item?.discount_percentage ? (
                                                <div
                                                    className="flex items-center justify-end text-xs w-full my-1 gap-1 md:gap-2 flex-wrap">
                                                    <del
                                                        className="text-gray-400">{item?.price?.toLocaleString("fa")}</del>
                                                    <div
                                                        className="w-[32px] text-white items-center text-xs font-medium justify-center h-[18px] flex rounded bg-red-500">
                                                        {item?.discount_percentage?.toLocaleString("fa")}
                                                        <Percent className="w-3 h-3"/>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div
                                                className="w-full flex items-center justify-end gap-1 font-black md:text-xl text-black">
                                                {item?.sale_price?.toLocaleString("fa")}
                                                <div className="text-xs">تومان</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-b border-line px-2 w-full"></div>
                                </div>
                            ))}
                        </div>
                        <div
                            className="w-full flex justify-between items-center px-5 py-4 absolute -bottom-3 left-0 bg-white h-24 border-t border-line rounded-b-[20px]">
                            <div className="text-base text-start">
                                <div className="text-past-price">جمع کل</div>
                                <div className="mt-3">
                                    {items
                                        ?.map((item) => item?.sale_price)
                                        .reduce((a, b) => Number(a) + Number(b), 0)
                                        ?.toLocaleString("fa")}{" "}
                                    تومان
                                </div>
                            </div>
                            <Link href={"/shopping-bag"}
                                  className="w-[220px] h-[48px] rounded-2xl mt-2 bg-orange-primary active:scale-95 transition-all items-center justify-center flex text-white duration-200 text-sm">
                                رفتن به سبد خرید
                            </Link>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default BagButtonComponent;
